import { useState, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "@/lib/safe-toast";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { useAuth } from "@/contexts/useAuth";
import { hasSupabaseConfig } from "@/config";
import { getToken } from "@/auth";
import { trackEvent } from "@/api";
import { Button, Input } from "@/components/ui";
import { cn, toErrorDisplayMessage, toDisplayString, toSafeReactNode } from "@/lib/utils";

type PasswordFormData = {
  email: string;
  password: string;
};

const ALLOWED_RETURN_PATHS = [
  "/",
  "/leads",
  "/crm",
  "/usage",
  "/billing",
  "/team",
  "/settings",
  "/referrals",
  "/affiliate",
  "/lead-signals",
  "/reminders",
  "/support",
  "/admin",
];

function isAllowedReturnPath(path: string): boolean {
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  const base = path.split("?")[0].split("#")[0];
  return ALLOWED_RETURN_PATHS.some((p) => p === base || base.startsWith(p + "/"));
}

export function LoginForm() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const passwordSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, toDisplayString(t("login.emailRequired")))
          .email(toDisplayString(t("login.invalidEmail"))),
        password: z.string().min(8, toDisplayString(t("login.minPassword"))),
      }),
    [t, i18n.language]
  );
  const searchParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : null;
  const search = searchParams
    ? {
        extensionAuth: searchParams.get("extensionAuth") ?? undefined,
        redirect: searchParams.get("redirect") ?? undefined,
        from: searchParams.get("from") ?? undefined,
      }
    : undefined;
  const { signIn, signUp, signInWithMagicLink, signInWithOAuth } = useAuth();
  const [tab, setTab] = useState<"password" | "magic">("password");
  const [magicEmail, setMagicEmail] = useState("");
  const [magicMessage, setMagicMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(passwordSchema as any),
    defaultValues: { email: "", password: "" },
  });

  if (!hasSupabaseConfig()) {
    return (
      <p className="text-sm text-danger">{toDisplayString(t("login.configMissing"))}</p>
    );
  }

  const onPasswordSubmit = async (data: PasswordFormData) => {
    setLoading(true);
    const res = await signIn(data.email.trim(), data.password);
    if (res.error) {
      setLoading(false);
      toast.error(toErrorDisplayMessage(res.error));
      return;
    }

    const token = await getToken();
    setLoading(false);
    if (!token) {
      toast.error(t("errors.session_not_ready"));
      return;
    }

    const returnSearch =
      search?.extensionAuth && search?.redirect
        ? { extensionAuth: search.extensionAuth, redirect: search.redirect }
        : undefined;
    const targetPath =
      search?.from && isAllowedReturnPath(search.from) ? search.from : "/";
    navigate({ to: targetPath, replace: true, search: returnSearch });
  };

  const onRegister = async (data: PasswordFormData) => {
    setLoading(true);
    const res = await signUp(data.email.trim(), data.password);
    setLoading(false);
    if (res.error) {
      toast.error(toErrorDisplayMessage(res.error));
      return;
    }
    trackEvent("signup_completed", null).catch(() => {});
    toast.success(t("login.confirmEmailThenSignIn"));
  };

  const handleMagicLink = async () => {
    const em = magicEmail.trim();
    if (!em) {
      setMagicMessage(toDisplayString(t("login.enterEmail")));
      return;
    }
    setMagicMessage("");
    setLoading(true);
    const res = await signInWithMagicLink(em);
    setLoading(false);
    if (res.error) {
      toast.error(toErrorDisplayMessage(res.error));
      return;
    }
    setMagicMessage(toDisplayString(t("login.magicLinkSent")));
  };

  const handleOAuth = async (provider: "google" | "apple") => {
    setOauthLoading(provider);
    const res = await signInWithOAuth(provider);
    setOauthLoading(null);
    if (res.error) {
      toast.error(toErrorDisplayMessage(res.error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-xs font-medium text-text-muted">{toDisplayString(t("login.signInWith"))}</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            disabled={loading || oauthLoading !== null}
            onClick={() => handleOAuth("google")}
            className="flex-1 font-medium"
          >
            {oauthLoading === "google" ? toDisplayString(t("login.redirecting")) : toDisplayString(t("login.google"))}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={loading || oauthLoading !== null}
            onClick={() => handleOAuth("apple")}
            className="flex-1 font-medium"
          >
            {oauthLoading === "apple" ? toDisplayString(t("login.redirecting")) : toDisplayString(t("login.apple"))}
          </Button>
        </div>
      </div>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-surface-2 px-2 text-text-muted">{toDisplayString(t("login.or"))}</span>
        </div>
      </div>
      <div className="flex rounded-lg border border-border bg-surface p-1" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "password"}
          onClick={() => setTab("password")}
          className={cn(
            "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors",
            tab === "password"
              ? "bg-surface-2 text-text shadow-sm"
              : "text-text-muted hover:bg-surface-2/50 hover:text-text"
          )}
        >
          {toDisplayString(t("login.emailPassword"))}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "magic"}
          onClick={() => setTab("magic")}
          className={cn(
            "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors",
            tab === "magic"
              ? "bg-surface-2 text-text shadow-sm"
              : "text-text-muted hover:bg-surface-2/50 hover:text-text"
          )}
        >
          {toDisplayString(t("login.magicLinkTab"))}
        </button>
      </div>

      {tab === "password" && (
        <form
          onSubmit={handleSubmit(onPasswordSubmit)}
          className="space-y-3"
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-text">
              {toDisplayString(t("login.emailLabel"))}
            </label>
            <Input
              type="email"
              placeholder={toDisplayString(t("login.emailPlaceholder"))}
              className="bg-bg"
              {...register("email")}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-danger">{toSafeReactNode(errors.email?.message)}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text">
              {toDisplayString(t("login.passwordLabel"))}
            </label>
            <Input
              type="password"
              className="bg-bg"
              {...register("password")}
            />
            {errors.password && (
              <p className="mt-1 text-xs text-danger">{toSafeReactNode(errors.password?.message)}</p>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              className="min-w-[120px] font-semibold"
            >
              {toDisplayString(t("login.signIn"))}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={loading}
              onClick={handleSubmit(onRegister)}
              className="min-w-[120px] font-medium"
            >
              {toDisplayString(t("login.signUp"))}
            </Button>
          </div>
        </form>
      )}

      {tab === "magic" && (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-text">
              {toDisplayString(t("login.emailLabel"))}
            </label>
            <Input
              type="email"
              placeholder={toDisplayString(t("login.emailPlaceholder"))}
              value={magicEmail}
              onChange={(e) => setMagicEmail(e.target.value)}
              className="min-w-[140px] bg-bg"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={handleMagicLink}
            className="font-medium"
          >
            {toDisplayString(t("login.sendMagicLinkButton"))}
          </Button>
          {magicMessage && (
            <p className="text-sm text-text-muted">{toDisplayString(magicMessage)}</p>
          )}
        </div>
      )}
    </div>
  );
}
